// /faqs — Customer-friendly product FAQs grouped by category.
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import Reveal from "@/components/layout/Reveal";
import {
  HelpCircle,
  Droplets,
  Cpu,
  TrendingUp,
  Wrench,
  Banknote,
  LifeBuoy,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const CATEGORIES = [
  {
    id: "basics",
    icon: Droplets,
    label: "Hot Water Heat Pump Basics",
    items: [
      [
        "What is a hot water heat pump?",
        "A hot water heat pump uses heat pump technology to move heat from the surrounding air into your water tank, instead of relying only on a traditional electric element or burning gas.",
      ],
      [
        "Why switch from electric or gas hot water?",
        "Older electric cylinders and gas systems can be expensive to run. A modern hot water heat pump can reduce running costs and emissions while providing reliable stored hot water.",
      ],
      [
        "Will it work in New Zealand conditions?",
        "Yes. AeroMax recommends systems suitable for everyday Kiwi household conditions, subject to correct sizing and installation.",
      ],
    ],
  },
  {
    id: "neopower",
    icon: Cpu,
    label: "NeoPower Black Shield",
    items: [
      [
        "Why does AeroMax recommend NeoPower Black Shield?",
        "AeroMax recommends NeoPower Black Shield because it offers a premium all-in-one design, inverter heat pump technology, R290 natural refrigerant, modern styling and strong performance for residential hot water upgrades.",
      ],
      [
        "What size do I need?",
        "Most households will suit a 270L system, but the right size depends on household size, showers per day, bathrooms, usage patterns and site conditions. AeroMax confirms this during assessment.",
      ],
      [
        "What warranty does NeoPower Black Shield have?",
        "NeoPower Black Shield has a manufacturer warranty structure that includes up to 7 years, subject to the manufacturer's warranty terms and correct installation. Confirm final warranty details in your quote.",
      ],
    ],
  },
  {
    id: "savings",
    icon: TrendingUp,
    label: "Savings & Calculator",
    items: [
      [
        "Are calculator savings guaranteed?",
        "No. The calculator provides an estimate only. Actual savings depend on usage, tariffs, climate, installation quality, settings, existing system efficiency and whether gas supply charges can be removed.",
      ],
      [
        "Why does the calculator show 15-year savings?",
        "Hot water systems are long-term household assets. The calculator shows estimated long-term savings using the assumptions displayed in the report, including system life and energy price escalation.",
      ],
      [
        "Can I email myself the estimate?",
        "Yes. After completing the calculator, customers can email their estimate and use it as a starting point for assessment or finance discussions.",
      ],
    ],
  },
  {
    id: "installation",
    icon: Wrench,
    label: "Installation & Assessment",
    items: [
      [
        "Do I need a home assessment?",
        "Yes. A site check helps confirm the best location, plumbing/electrical requirements, access, drainage and whether the selected system is appropriate.",
      ],
      [
        "Can AeroMax remove my old unit?",
        "Removal options depend on site conditions and installer availability. This can be confirmed during the quote and assessment process.",
      ],
      [
        "How long does installation take?",
        "Many straightforward replacements may be completed within a day, but timing depends on the home, existing system and installation requirements.",
      ],
    ],
  },
  {
    id: "finance",
    icon: Banknote,
    label: "Green Loans & Finance",
    items: [
      [
        "Can I use a green home loan for a hot water heat pump?",
        "Some NZ banks offer green or energy-efficiency home loan top-ups that may support eligible hot water heat pump installations. Eligibility is determined by the bank.",
      ],
      [
        "Does AeroMax apply for the loan for me?",
        "No. AeroMax does not provide lending or financial advice. We can help prepare a quote, product details and savings estimate that customers may use when speaking with their bank.",
      ],
      [
        "Which banks should I check?",
        "Customers may check options with ANZ, ASB, BNZ, Westpac and Kiwibank. Eligibility, rates and conditions vary by bank.",
      ],
    ],
  },
  {
    id: "support",
    icon: LifeBuoy,
    label: "Support",
    items: [
      [
        "Who do I contact after installation?",
        "Contact support@aeromaxenergy.co.nz for support and warranty questions.",
      ],
      [
        "Do you service all of New Zealand?",
        "AeroMax is building a New Zealand-wide assessment and installation network. Availability may vary by region.",
      ],
      [
        "Can I speak to someone at a home show?",
        "Yes. Visit the Home Shows page to see upcoming AeroMax home show regions and register interest.",
      ],
    ],
  },
];

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-16 sm:pt-20 sm:pb-20">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <HelpCircle className="h-3 w-3 text-[#10B981]" /> FAQs
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Common questions, <span className="text-[#10B981]">clearly answered</span>.
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-3xl">
            From the basics of hot water heat pumps to NeoPower Black Shield, calculator assumptions, installation, finance and post-install support — everything in one place.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <div className="sticky top-20 z-30 bg-white/85 backdrop-blur-md border-b border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#475569] shrink-0 pr-2">
          Jump to:
        </span>
        {CATEGORIES.map((c) => (
          <a
            key={c.id}
            href={`#${c.id}`}
            data-testid={`faq-anchor-${c.id}`}
            className="text-[12px] font-semibold text-[#0B132B] hover:text-[#10B981] whitespace-nowrap px-3 py-1.5 rounded-full hover:bg-[#10B981]/8 transition-colors"
          >
            {c.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function CategoryBlock({ cat }) {
  return (
    <section id={cat.id} data-testid={`faq-category-${cat.id}`} className="scroll-mt-32">
      <div className="flex items-center gap-3 mb-5">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981]">
          <cat.icon className="h-5 w-5" />
        </span>
        <div>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
            Category
          </span>
          <h2 className="font-heading mt-1.5 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">
            {cat.label}
          </h2>
        </div>
      </div>
      <Accordion
        type="single"
        collapsible
        data-testid={`faq-accordion-${cat.id}`}
        className="space-y-3"
      >
        {cat.items.map(([q, a], i) => (
          <AccordionItem
            key={i}
            value={`${cat.id}-${i}`}
            className="border border-[#E2E8F0] rounded-2xl px-6 bg-white hover:border-[#10B981]/30 hover:shadow-md hover:shadow-emerald-500/5 transition-all"
          >
            <AccordionTrigger className="text-left font-heading text-base sm:text-lg font-semibold text-[#0B132B] hover:no-underline py-5">
              {q}
            </AccordionTrigger>
            <AccordionContent className="text-[#475569] leading-relaxed pb-5 text-sm sm:text-[15px]">
              {a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function StillStuckCTA() {
  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-[#E2E8F0] bg-gradient-to-br from-white via-white to-[#F0FDF4] p-8 sm:p-12">
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
            <Sparkles className="h-3 w-3" /> Still stuck?
          </span>
          <h2 className="font-heading mt-4 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight max-w-2xl">
            Can't find what you're looking for?
          </h2>
          <p className="mt-3 text-[#475569] max-w-2xl leading-relaxed">
            Send us a quick note and the right AeroMax specialist will be in touch.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="faq-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30"
            >
              Contact AeroMax <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/savings-calculator"
              data-testid="faq-cta-calculator"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] hover:border-[#0B132B] text-[#0B132B] px-6 py-3 font-semibold transition-all"
            >
              Run Savings Calculator
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function FAQ() {
  return (
    <div data-testid="faq-page" className="bg-white">
      <Hero />
      <CategoryNav />
      <section className="py-14 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 space-y-14 sm:space-y-20">
          {CATEGORIES.map((cat) => (
            <CategoryBlock key={cat.id} cat={cat} />
          ))}
        </div>
      </section>
      <StillStuckCTA />
    </div>
  );
}
